// covered-by: qa/rickrubin/tests/specs/cca/ask.puntt.notifications.settings.spec.ts
import {
  askPunttNotificationPrefsSchema,
  defaultAskPunttNotificationPrefs,
  type AskPunttNotificationPrefs,
} from "@mg/schemas/src/commons/askPunttNotificationPrefs";
import { Card, Flex, Heading, Switch, Text } from "@radix-ui/themes";
import { useCallback, useEffect, useState } from "react";

import { useUI } from "../../../contexts/ui";
import {
  getUserProfile,
  updateCurrentUserProfile,
} from "../../../services/userProfile";
import { AskPunttDesktopNotificationsToggleControl } from "../../ask-puntt/components/AskPunttDesktopNotificationsToggleControl";

type EmailPrefKey = keyof AskPunttNotificationPrefs;

const EMAIL_PREF_ROWS: {
  key: EmailPrefKey;
  label: string;
  description: string;
  testId: string;
}[] = [
  {
    key: "emailNewMessage",
    label: "New chat messages",
    description:
      "Email when Ask Puntt posts a new assistant reply or a teammate sends a message in a shared chat.",
    testId: "settings-notifications-email-new-message",
  },
  {
    key: "emailReviewComplete",
    label: "Review complete",
    description: "Email when an artwork review finishes in a project or chat.",
    testId: "settings-notifications-email-review-complete",
  },
  {
    key: "emailArtifactReady",
    label: "Artifact ready",
    description:
      "Email when a canvas document or review artifact is ready to open.",
    testId: "settings-notifications-email-artifact-ready",
  },
  {
    key: "emailProjectActivity",
    label: "Project activity",
    description:
      "Email when you are added to a project or other project membership updates.",
    testId: "settings-notifications-email-project-activity",
  },
];

export function NotificationsSettingsView() {
  const { notify } = useUI();
  const [prefs, setPrefs] = useState<AskPunttNotificationPrefs>(
    defaultAskPunttNotificationPrefs,
  );
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState<EmailPrefKey | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const profile = await getUserProfile();
        if (cancelled) return;
        setPrefs(
          askPunttNotificationPrefsSchema.parse(
            profile.askPunttNotificationPrefs ?? {},
          ),
        );
      } catch {
        if (!cancelled) {
          notify({
            variant: "error",
            title: "Could not load notification settings",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [notify]);

  const persistPref = useCallback(
    async (key: EmailPrefKey, next: boolean) => {
      const previous = prefs;
      const nextPrefs = { ...prefs, [key]: next };
      setPrefs(nextPrefs);
      setSavingKey(key);
      try {
        await updateCurrentUserProfile({
          askPunttNotificationPrefs: nextPrefs,
        });
      } catch {
        setPrefs(previous);
        notify({
          variant: "error",
          title: "Could not save notification setting",
        });
      } finally {
        setSavingKey(null);
      }
    },
    [notify, prefs],
  );

  return (
    <div
      data-testid="settings-notifications-page"
      className="flex h-full flex-col overflow-hidden"
    >
      <article className="flex-1 space-y-6 overflow-y-auto px-10 py-6">
        <header className="flex flex-col gap-2">
          <Heading as="h1" size="7" weight="bold">
            Notifications
          </Heading>
          <Text size="2" color="gray">
            Choose how Ask Puntt alerts you when you are away from the tab.
          </Text>
        </header>

        <Card data-testid="settings-notifications-desktop-card">
          <Flex direction="column" gap="3" p="4">
            <Heading as="h2" size="4" weight="medium">
              Desktop
            </Heading>
            <Text size="2" color="gray">
              Show browser notifications while Puntt is open in another tab.
            </Text>
            <AskPunttDesktopNotificationsToggleControl />
          </Flex>
        </Card>

        <Card data-testid="settings-notifications-email-card">
          <Flex direction="column" gap="4" p="4">
            <Flex direction="column" gap="1">
              <Heading as="h2" size="4" weight="medium">
                Email
              </Heading>
              <Text size="2" color="gray">
                Emails are sent from Puntt when these events happen and you are
                not actively reading the chat.
              </Text>
            </Flex>
            {loading ? (
              <Text
                size="2"
                color="gray"
                data-testid="settings-notifications-loading"
              >
                Loading preferences…
              </Text>
            ) : (
              EMAIL_PREF_ROWS.map((row) => (
                <Flex
                  key={row.key}
                  align="center"
                  justify="between"
                  gap="4"
                  data-testid={row.testId}
                >
                  <Flex direction="column" gap="1" style={{ flex: 1 }}>
                    <Text size="2" weight="medium">
                      {row.label}
                    </Text>
                    <Text size="1" color="gray">
                      {row.description}
                    </Text>
                  </Flex>
                  <Switch
                    data-testid={`${row.testId}-switch`}
                    aria-label={row.label}
                    checked={prefs[row.key]}
                    disabled={savingKey === row.key}
                    onCheckedChange={(checked) => {
                      void persistPref(row.key, checked);
                    }}
                  />
                </Flex>
              ))
            )}
          </Flex>
        </Card>
      </article>
    </div>
  );
}
